import React from "react";
import { Linkedin } from "lucide-react";
import { PageHeader } from "../components/pageheader";

/* ---- Assets (bilder) ---- */
import AnnaTImg from "@/assets/images/anna-t.png";
import MarieImg from "@/assets/images/marie-c.png";
import JohanImg from "@/assets/images/johan-r.png";
import AlexCImg from "@/assets/images/alex-c.png";
import AndreasHImg from "@/assets/images/andreas-h.png";
import MarkusRImg from "@/assets/images/markus-r.png";
import PatrickFImg from "@/assets/images/patrick-f.png";
import PatrikPImg from "@/assets/images/patrik-p.png";
import PatrikRImg from "@/assets/images/patrik-r.png";
import PhilipPImg from "@/assets/images/philip-p.png";
import ViktorNImg from "@/assets/images/viktor-n.png";
import AlexNybImg from "@/assets/images/alex-n.png";
import MagnusCImg from "@/assets/images/magnus-c.png";
import DamjanVImg from "@/assets/images/damjan-v.png";
import LyupchoKImg from "@/assets/images/lyupchu-k.png";
import VincentLImg from "@/assets/images/vincent-l.png";
import ChrisImg from "@/assets/images/chris.png";
import AvatarFallback from "@/assets/images/icon-dude.svg";
import { motion } from "framer-motion";
import CTAButton from "../components/button";

/* ---- Layout helper ---- */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* ---- Types ---- */
type Member = {
  name: string;
  title: string;
  bio?: string;
  email?: string;
  linkedin?: string;
  photo?: string;
  groups?: string[]; // ← kan tillhöra flera
};

/* ---- Data ---- */
const TEAM: Member[] = [
  {
    name: "Anna Tunberg",
    title: "Head of Operations",
    linkedin: "https://www.linkedin.com/in/annatunberg/",
    bio: "9+ years in business development, branding, sales and recruitment. Strategic yet hands-on, building structures and driving initiatives that support long-term growth for people and organizations.",
    photo: AnnaTImg,
  },
  {
    name: "Chris Evers",
    title: "Senior IT Sales Specialist",
    linkedin: "https://www.linkedin.com/in/christofer-evers/",
    bio: "Your first point of contact. Takes the time to understand your business and goals, then helps you find the right cloud and technology solutions and the right team to deliver them.",
    photo: ChrisImg,
  },
  {
    name: "Marie Coletti",
    title: "Project Manager",
    linkedin: "https://se.linkedin.com/in/marie-coletti-47688257",
    bio: "Project manager with 10+ years in software development. Roles such as project manager, team lead, manager and board member has contributed to a broad competence and experience.",
    photo: MarieImg,
    groups: ["Apps"],
  },
  {
    name: "Johan Rosengren",
    title: "UX Designer · Developer",
    linkedin: "https://www.linkedin.com/in/johanrosengren/",
    bio: "Experienced designer that loves work with both UI/UX design and front-end development. Bringing designs to life in HTML/CSS and JavaScript.",
    photo: JohanImg,
    groups: ["Apps"],
  },
  {
    name: "Magnus Christiansson",
    title: "Enterprise Architect · Platform Engineer",
    linkedin: "https://www.linkedin.com/in/magnuschristiansson",
    bio: "Devops and Platform Engineering and architecture. Passion for developer effectiveness and experience. Kubernetes, OpenShift, Backstage, Developer Platforms.",
    photo: MagnusCImg,
    groups: ["Platforms"],
  },
  {
    name: "Philip Persson",
    title: "Developer",
    linkedin: "https://www.linkedin.com/in/philip-persson-58b62870/",
    bio: "Passionate developer who thrives on collaboration and building great applications. A knack for bridging frontend and backend working with .NET, Node.js, React, Next.js, and their related technologies.",
    photo: PhilipPImg,
    groups: ["Apps"],
  },
  {
    name: "Patrick Fogwall",
    title: "Developer",
    linkedin: "https://se.linkedin.com/in/patrickfogwall",
    bio: "Software developer and architect working primarily with .NET, C#, Javascript, SQL Server, Episerver, inRiver PIM, Apptus eSales and CI/CD.",
    photo: PatrickFImg,
    groups: ["Apps"],
  },
  {
    name: "Patrik Pochill",
    title: "Developer",
    linkedin: "https://se.linkedin.com/in/patrik-pochill-a2899981",
    bio: "Software developer and solution architect with focus on C#, Azure, js/ts, React, Episerver, Inriver PIM, Umbraco, CI/CD. Connects code with business value.",
    photo: PatrikPImg,
    groups: ["Apps"],
  },
  {
    name: "Viktor Nilsson",
    title: "Developer",
    linkedin: "https://www.linkedin.com/in/viktor-nilsson86/",
    bio: "Frontend developer mainly working with React in Javascript and Typescript. Experiences include Redux, Emotion, SASS and Node.js.",
    photo: ViktorNImg,
    groups: ["Apps"],
  },
  {
    name: "Patrik Rizell",
    title: "Developer",
    linkedin: "https://www.linkedin.com/in/patrik-rizell-05a13333/",
    bio: "Fullstack developer with an interest in software architecture. Focus and in-depth experience in .NET, C#, Javascript/Typescript, React, GraphQL, microservice architecture.",
    photo: PatrikRImg,
    groups: ["Apps"],
  },

  {
    name: "Markus Rytterkull",
    title: "Developer",
    linkedin: "https://se.linkedin.com/in/markus-rytterkull-1549901",
    bio: "Multifunctional Software Developer and Trainer working primarily with web-oriented development on Microsoft platforms: .NET, Razor, C#, Javascript, CSS, SQL Server, Episerver and Umbraco.",
    photo: MarkusRImg,
    groups: ["Apps"],
  },
  {
    name: "Alexander Christiansson",
    title: "Developer",
    linkedin: "https://www.linkedin.com/in/alexander-christiansson-875046b2",
    bio: "Specializing in C#, .NET, JavaScript, and React. Delivers efficient, high-quality software solutions tailored to clients' needs.",
    photo: AlexCImg,
    groups: ["Apps"],
  },
  {
    name: "Andreas Hellberg",
    title: "Developer",
    linkedin: "https://www.linkedin.com/in/andreas-hellberg-79767370",
    bio: "Software developer and architect working primarily with .NET, C#, Javascript, SQL Server, Episerver, Apptus eSales and DevOps.",
    photo: AndreasHImg,
    groups: ["Apps"],
  },
  {
    name: "Lyupcho Kotev",
    title: "Developer · Platform Engineer",
    linkedin: "https://www.linkedin.com/in/ljupcho-kotev",
    bio: "Passionate Developer and Devops Engineer with an interest in Platform Engineering, eager improving developer effectiveness and experience in organisations.",
    photo: LyupchoKImg,
    groups: ["Platforms"],
  },
  {
    name: "Vincent Lam",
    title: "Full-Stack Developer",
    linkedin: "https://www.linkedin.com/in/vincent-lam-75b926293/",
    bio: "Versatile full-stack developer with 10+ years of programming. Specializes in web frameworks and API integrations across front- and back-end, driven to master every stack he works with.",
    photo: VincentLImg,
    groups: ["Apps"],
  },
  {
    name: "Damjan Velickovski",
    title: "Platform Engineer",
    linkedin: "https://www.linkedin.com/in/velickovski",
    bio: "Passionate and motivated developer and DevOps engineer with a strong interest in open source projects and technologies.",
    photo: DamjanVImg,
    groups: ["Platforms"],
  },
];

/* ---- Components ---- */
const MemberCard: React.FC<{ m: Member }> = ({ m }) => {
  return (
    <article className="flex flex-col items-center text-center rounded-2xl bg-[#0f1a18] p-8 h-full">
      {/* Bild */}
      <div className="relative">
        {m.photo ? (
          <img
            src={m.photo}
            alt={m.name}
            loading="lazy"
            className="h-32 w-32 rounded-full object-cover bg-[#152D2B] ring-1 ring-white/10"
          />
        ) : (
          <div className="h-32 w-32 rounded-full bg-[#152D2B] flex items-center justify-center ring-1 ring-white/10">
            <img src={AvatarFallback} alt="" className="h-8 w-8 opacity-70" />
          </div>
        )}
      </div>

      {/* Namn & roll */}
      <div className="mt-6 space-y-2 mb-4">
        <h3 className="font-semibold leading-tight text-lg">{m.name}</h3>
        <p className="text-sm text-white/70">{m.title}</p>
      </div>

      {/* Grupp-tags */}
      {m.groups && (
        <div className="mb-4 flex flex-wrap justify-center gap-2">
          {m.groups.map((g) => (
            <span
              key={g}
              className="rounded-full bg-[#FF893F]/10 px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#FF893F]"
            >
              {g}
            </span>
          ))}
        </div>
      )}

      {/* Bio */}
      {m.bio && (
        <p className="text-sm text-white/70 leading-relaxed max-w-xs">
          {m.bio}
        </p>
      )}

      {/* Pushar knappen till botten */}
      <div className="mt-auto pt-6">
        {m.linkedin && (
          <CTAButton
            href={m.linkedin}
            variant="small"
            target="_blank"
            rel="noreferrer"
            aria-label={`View ${m.name} on LinkedIn`}
          >
            View on LinkedIn
          </CTAButton>
        )}
      </div>
    </article>
  );
};

/* ---- Page ---- */
export default function TeamPage() {
  return (
    <section className="py-14">
      <Container>
        <PageHeader
          eyebrow="The team"
          title="People who get it done"
          text="A small, senior core team supported by a dedicated platform crew. We assemble the right team for every project."
        />
        {/* Alla i samma grid */}
        <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {TEAM.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 18, filter: "blur(6px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 22,
                delay: i * 0.08, // 👈 en-och-en baserat på index
              }}
            >
              <MemberCard m={m} />
            </motion.div>
          ))}
        </div>
        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="mb-8">
            Want to be part of something bold? We work with great people,
            ambitious brands, and real challenges. If you’re curious,
            collaborative, and love turning ideas into reality — let’s talk.
          </p>
          <CTAButton href="/hire">Get in touch</CTAButton>
        </div>
      </Container>
    </section>
  );
}
